import { Component } from '@theme/component';
import * as store from '@theme/saved-for-later';
import { formatMoney } from '@theme/money-formatting';
import { insertPendingRow } from '@theme/cart-pending-row';
import { addToCart as postCartAdd, cartSectionIds, themeRoutes } from '@theme/ca-cart-add';

const DAY = 86400000;

function agoLabel(iso, strings) {
  const at = Date.parse(iso || '');
  if (!Number.isFinite(at)) return '';
  const days = Math.floor((Date.now() - at) / DAY);
  if (days < 1) return strings.saved_today;
  if (days === 1) return strings.saved_yesterday;
  if (days < 14) return strings.saved_days.replace('[count]', String(days));
  if (days < 60) return strings.saved_weeks.replace('[count]', String(Math.floor(days / 7)));
  return strings.saved_months.replace('[count]', String(Math.floor(days / 30)));
}

const safeUrl = (value) => {
  const raw = String(value ?? '').trim();
  return /^(https?:|\/\/|\/)/i.test(raw) ? raw : '';
};

/**
 * An empty array is truthy, so sending properties verbatim makes Shopify reject the add.
 * @param {unknown} value
 * @returns {object|null} a plain object, or null when there is nothing to send
 */
function normalizeProperties(value) {
  let source = value;
  if (Array.isArray(source)) {
    source = Object.fromEntries(
      source.filter((pair) => Array.isArray(pair) && pair.length === 2).map(([key, val]) => [key, val])
    );
  }
  if (!source || typeof source !== 'object') return null;
  const entries = Object.entries(source).filter(([, val]) => val !== null && val !== '');
  return entries.length ? Object.fromEntries(entries) : null;
}

/** djb2 — short, stable, and cheap enough to run on every render. */
function fingerprint(value) {
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0;
  return (hash >>> 0).toString(36);
}

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[char]);

class SavedForLater extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (this.wired) return;
    this.wired = true;

    this.strings = this.readStrings();
    this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
    this.currency = this.getAttribute('data-currency') || 'USD';
    this.mode = this.getAttribute('data-mode') === 'swap' ? 'swap' : 'inline';
    this.root = this.closest('[data-sfl-root]') || document;

    this.onClick = this.onClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onStoreUpdate = this.onStoreUpdate.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);

    this.root.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit);
    document.addEventListener('saved-for-later:update', this.onStoreUpdate);
    document.addEventListener('cart:update', this.onStoreUpdate);

    this.dialog = this.closest('dialog');
    if (this.dialog) this.dialog.addEventListener('close', this.onDialogClose);

    this.view = 'cart';
    this.cartLabelledBy = this.dialog?.getAttribute('aria-labelledby') || null;

    this.observer = new MutationObserver(() => {
      clearTimeout(this.reflowTimer);
      this.reflowTimer = setTimeout(() => this.render(), 0);
    });
    if (this.root !== document) {
      this.observer.observe(this.root, { childList: true, subtree: true });
    }

    this.render();
  }

  updatedCallback() {
    super.updatedCallback();
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.root?.removeEventListener('click', this.onClick);
    this.removeEventListener('submit', this.onSubmit);
    document.removeEventListener('saved-for-later:update', this.onStoreUpdate);
    document.removeEventListener('cart:update', this.onStoreUpdate);
    this.dialog?.removeEventListener('close', this.onDialogClose);
    this.observer?.disconnect();
    clearTimeout(this.reflowTimer);
    this.wired = false;
  }

  readStrings() {
    let parsed;
    try {
      parsed = JSON.parse(this.querySelector('[data-sfl-strings]')?.textContent || '{}');
    } catch {
      return {};
    }
    const decoder = document.createElement('textarea');
    return Object.fromEntries(
      Object.entries(parsed).map(([key, value]) => {
        if (typeof value !== 'string') return [key, value];
        decoder.innerHTML = value;
        return [key, decoder.value];
      })
    );
  }

  /* ------------------------------------------------------------- rendering */

  get panel() {
    return this.querySelector('[data-sfl-panel]');
  }

  get teaser() {
    return this.root.querySelector?.('[data-sfl-teaser]') ?? null;
  }

  get cartView() {
    return this.root.querySelector?.('cart-items-component') ?? null;
  }

  #write(node, html) {
    if (!node) return false;
    const signature = fingerprint(html);
    if (node.dataset.sflSig === signature) return false;
    node.innerHTML = html;
    node.dataset.sflSig = signature;
    return true;
  }

  onStoreUpdate() {
    queueMicrotask(() => this.render());
  }

  onDialogClose() {
    this.showCart();
  }

  render() {
    try {
      const items = store.list();
      this.renderTeaser(items);
      this.syncRailSuppression(items.length > 0);
      this.syncRailExclusions(items);
      this.renderList(items);
      this.renderReminder(items);

      const heading = this.querySelector('[data-sfl-count]');
      const count = String(items.length);
      if (heading && heading.textContent !== count) heading.textContent = count;

      if (this.mode === 'inline') {
        const panel = this.panel;
        if (panel) panel.hidden = !items.length && !this.errorBanner;
        return;
      }

      if (!items.length && this.view === 'saved') this.showCart();
      else this.applyView();
    } finally {
      this.observer?.takeRecords();
    }
  }

  applyView() {
    if (this.mode !== 'swap') return;
    const cart = this.cartView;
    const panel = this.panel;
    if (!cart || !panel) return;

    const saved = this.view === 'saved';
    if (cart.hidden !== saved) cart.hidden = saved;
    if (panel.hidden === saved) panel.hidden = !saved;

    if (!this.dialog || this.dialog.hasAttribute('aria-busy')) return;
    const target = saved ? `sfl-panel-heading-${this.mode}` : this.cartLabelledBy;
    if (!target) {
      if (this.dialog.getAttribute('aria-labelledby') === `sfl-panel-heading-${this.mode}`) {
        this.dialog.removeAttribute('aria-labelledby');
      }
      return;
    }
    if (this.dialog.getAttribute('aria-labelledby') !== target) {
      this.dialog.setAttribute('aria-labelledby', target);
    }
  }

  /** One saved product per slide, swipeable. Manual only. */
  renderTeaser(items) {
    const teaser = this.teaser;
    if (!teaser) return;

    if (!items.length) {
      teaser.hidden = true;
      this.#write(teaser, '');
      return;
    }

    const count = items.length;
    const slides = items.map((entry, index) => this.shelfSlide(entry, index)).join('');

    const nav = count > 1 ? '<span class="sfl-shelf__nav" data-sfl-nav></span>' : '';

    const overflow =
      count > 3
        ? `<button type="button" class="sfl-shelf__all" data-sfl-open>${escapeHtml(
            (this.strings.shelf_view_all || 'View all ([count])').replace('[count]', String(count))
          )}</button>`
        : '';

    const dots =
      count > 1
        ? '<span class="sfl-shelf__dots" role="tablist">' +
          items
            .map(
              (entry, index) =>
                `<button type="button" class="sfl-shelf__dot" role="tab" data-sfl-dot="${index}" aria-label="${escapeHtml(
                  `${index + 1} / ${count}`
                )}"></button>`
            )
            .join('') +
          '</span>'
        : '';

    const html =
      '<div class="sfl-shelf">' +
      '<div class="sfl-shelf__head">' +
      `<span class="sfl-shelf__label">${escapeHtml(this.strings.heading || 'Saved for later')}` +
      (count > 1 ? `<span class="sfl-shelf__count"> · ${count}</span>` : '') +
      '</span>' +
      overflow +
      nav +
      '</div>' +
      `<div class="sfl-shelf__track ca-no-scrollbar" data-sfl-track>${slides}</div>` +
      dots +
      '<span class="sfl-shelf__error" data-sfl-error role="status" aria-live="polite" hidden></span>' +
      '</div>';

    const rewritten = this.#write(teaser, html);
    teaser.hidden = false;
    if (rewritten) {
      this.renderShelfNav();
      this.bindShelf(items.length);
    }
  }

  /** Clone the theme's canonical carousel arrows rather than drawing our own. */
  renderShelfNav() {
    const nav = this.teaser?.querySelector('[data-sfl-nav]');
    const source = this.querySelector('[data-sfl-arrows]');
    if (!nav || !source || nav.childElementCount) return;

    const clone = source.content.cloneNode(true);
    clone.querySelectorAll('.slideshow-control').forEach((button, index) => {
      // These carry slideshow-component bindings that mean nothing here.
      button.removeAttribute('on:click');
      button.removeAttribute('ref');
      button.setAttribute(index === 0 ? 'data-sfl-prev' : 'data-sfl-next', '');
      button.setAttribute(
        'aria-label',
        (index === 0 ? this.strings.prev : this.strings.next) || button.getAttribute('aria-label') || ''
      );
    });
    nav.appendChild(clone);
  }

  shelfSlide(entry, index) {
    const media = entry.img
      ? `<img class="sfl-shelf__image" src="${escapeHtml(safeUrl(entry.img))}" alt="" width="64" height="64" loading="${
          index === 0 ? 'eager' : 'lazy'
        }">`
      : '<span class="sfl-shelf__image sfl-shelf__image--blank"></span>';

    const meta = [formatMoney(entry.p, this.moneyFormat, this.currency), agoLabel(entry.saved_at, this.strings)]
      .filter(Boolean)
      .join(' · ');

    return (
      '<div class="sfl-shelf__slide">' +
      `<div class="sfl-shelf__body">${media}<div class="sfl-shelf__text">` +
      `<a class="sfl-shelf__title" href="${escapeHtml(safeUrl(entry.u) || '#')}">${escapeHtml(entry.t)}</a>` +
      (entry.vt ? `<span class="sfl-shelf__variant">${escapeHtml(entry.vt)}</span>` : '') +
      `<span class="sfl-shelf__meta">${escapeHtml(meta)}</span>` +
      '</div></div>' +
      `<button type="button" class="button button-secondary sfl-shelf__cta" data-sfl-restore="${escapeHtml(
        entry.k
      )}">${escapeHtml(this.strings.move_to_cart || 'Move to cart')}</button>` +
      `<button type="button" class="sfl-shelf__drop" data-sfl-drop="${escapeHtml(entry.k)}">${escapeHtml(
        this.strings.remove || 'Remove'
      )}</button>` +
      '</div>'
    );
  }

  /** Track state lives on the element so a re-render cannot reset it. */
  bindShelf(count) {
    const track = this.teaser?.querySelector('[data-sfl-track]');
    if (!track) return;

    this.shelfIndex = Math.min(this.shelfIndex || 0, Math.max(0, count - 1));
    this.shelfCount = count;

    track.addEventListener(
      'scroll',
      () => {
        clearTimeout(this.shelfScrollTimer);
        this.shelfScrollTimer = setTimeout(() => {
          const width = track.clientWidth || 1;
          this.shelfIndex = Math.round(track.scrollLeft / width);
          this.markShelfDot();
        }, 90);
      },
      { passive: true }
    );

    requestAnimationFrame(() => {
      track.scrollTo({ left: this.shelfIndex * track.clientWidth, behavior: 'auto' });
      this.markShelfDot();
    });
  }

  markShelfDot() {
    const dots = this.teaser?.querySelectorAll('[data-sfl-dot]');
    if (!dots?.length) return;
    dots.forEach((dot, index) => {
      const active = index === this.shelfIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', String(active));
    });
  }

  goToSlide(index) {
    const track = this.teaser?.querySelector('[data-sfl-track]');
    if (!track) return;
    const max = Math.max(0, (this.shelfCount || 1) - 1);
    this.shelfIndex = Math.min(Math.max(index, 0), max);
    track.scrollTo({
      left: this.shelfIndex * track.clientWidth,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    this.markShelfDot();
  }


  /** Hide saved products from the rails; the rail only excludes what is in the cart. */
  syncRailExclusions(items) {
    const saved = new Set(items.map((entry) => String(entry.pid)).filter((id) => id && id !== 'null'));

    this.root.querySelectorAll?.('.cart-upsell').forEach((rail) => {
      const cards = rail.querySelectorAll('.cart-upsell__card[data-product-id]');
      if (!cards.length) return;

      let visible = 0;
      cards.forEach((card) => {
        const hide = saved.has(String(card.getAttribute('data-product-id')));
        if (card.hidden !== hide) card.hidden = hide;
        if (!hide) visible += 1;
      });

      const emptied = visible === 0;
      if (rail.hidden !== emptied) rail.hidden = emptied;
    });
  }

  syncRailSuppression(hasSaved) {
    const scroller = this.root.querySelector?.('.cart-drawer__content');
    if (!scroller) return;
    scroller.classList.toggle('cart-drawer__content--sfl', Boolean(hasSaved));
  }

  renderList(items) {
    const target = this.querySelector('[data-sfl-items]');
    if (!target) return;

    if (!items.length) {
      this.#write(target, `<p class="sfl-empty">${escapeHtml(this.strings.empty)}</p>`);
      return;
    }

    const html = items
      .map((entry) => {
        const meta = [
          Number(entry.q) > 1 ? this.strings.quantity.replace('[count]', String(entry.q)) : '',
          agoLabel(entry.saved_at, this.strings),
        ]
          .filter(Boolean)
          .join(' · ');

        const media = entry.img
          ? `<img class="sfl-item__image" src="${escapeHtml(safeUrl(entry.img))}" alt="" width="68" height="68" loading="lazy">`
          : '<span class="sfl-item__image sfl-item__image--blank"></span>';

        return (
          `<li class="sfl-item" data-sfl-key="${escapeHtml(entry.k)}">${media}` +
          '<div class="sfl-item__body">' +
          '<span class="sfl-item__title-row">' +
          `<a class="sfl-item__title" href="${escapeHtml(safeUrl(entry.u) || '#')}">${escapeHtml(entry.t)}</a>` +
          `<span class="sfl-item__price">${formatMoney(entry.p, this.moneyFormat, this.currency)}</span>` +
          '</span>' +
          (entry.vt ? `<span class="sfl-item__variant">${escapeHtml(entry.vt)}</span>` : '') +
          (meta ? `<span class="sfl-item__meta">${escapeHtml(meta)}</span>` : '') +
          '<span class="sfl-item__actions">' +
          `<button type="button" class="sfl-item__action" data-sfl-restore="${escapeHtml(entry.k)}">${escapeHtml(
            this.strings.move_to_cart
          )}</button>` +
          `<button type="button" class="sfl-item__action sfl-item__action--quiet" data-sfl-drop="${escapeHtml(
            entry.k
          )}">${escapeHtml(this.strings.remove)}</button>` +
          '</span>' +
          `<span class="sfl-item__error" data-sfl-error role="status" aria-live="polite" hidden></span>` +
          '</div></li>'
        );
      })
      .join('');

    this.#write(target, html);
  }

  renderReminder(items) {
    const block = this.querySelector('[data-sfl-reminder]');
    if (!block) return;
    const address = store.reminderAddress();
    block.hidden = !items.length;
    block.querySelector('[data-sfl-reminder-form]')?.toggleAttribute('hidden', Boolean(address));
    const done = block.querySelector('[data-sfl-reminder-done]');
    if (done) done.hidden = !address;
  }

  /* -------------------------------------------------------------- view swap */

  showSaved() {
    if (this.mode !== 'swap' || this.view === 'saved') return;
    const scroller = this.root.querySelector?.('.cart-drawer__content');
    this.cartScrollTop = scroller ? scroller.scrollTop : 0;

    this.view = 'saved';
    this.cartLabelledBy = this.dialog?.getAttribute('aria-labelledby') || this.cartLabelledBy;
    this.applyView();
    this.panel?.querySelector('[data-sfl-heading]')?.focus();
  }

  showCart() {
    if (this.mode !== 'swap' || this.view === 'cart') return;

    this.view = 'cart';
    this.applyView();

    const scroller = this.root.querySelector?.('.cart-drawer__content');
    if (scroller && this.cartScrollTop != null) scroller.scrollTop = this.cartScrollTop;

    const fallback =
      this.teaser?.querySelector('[data-sfl-open]') || this.root.querySelector?.('[ref="closeButton"]');
    if (fallback instanceof HTMLElement) fallback.focus();
  }

  /* --------------------------------------------------------------- actions */

  onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // A re-render can detach this button mid-dispatch; the drawer reads that as an outside click.
    if (
      target.closest(
        '[data-sfl-open],[data-sfl-close],[data-sfl-back],[data-sfl-prev],[data-sfl-next],[data-sfl-dot],[data-sfl-restore],[data-sfl-drop],[data-sfl-save]'
      )
    ) {
      event.stopPropagation();
    }

    const open = target.closest('[data-sfl-open]');
    if (open) {
      event.preventDefault();
      this.showSaved();
      return;
    }

    if (target.closest('[data-sfl-close]')) {
      event.preventDefault();
      this.closest('cart-drawer-component')?.close();
      return;
    }

    const back = target.closest('[data-sfl-back]');
    if (back) {
      event.preventDefault();
      this.showCart();
      return;
    }

    if (target.closest('[data-sfl-prev]')) {
      event.preventDefault();
      this.goToSlide((this.shelfIndex || 0) - 1);
      return;
    }

    if (target.closest('[data-sfl-next]')) {
      event.preventDefault();
      this.goToSlide((this.shelfIndex || 0) + 1);
      return;
    }

    const dot = target.closest('[data-sfl-dot]');
    if (dot) {
      event.preventDefault();
      this.goToSlide(Number(dot.getAttribute('data-sfl-dot')) || 0);
      return;
    }

    const restore = target.closest('[data-sfl-restore]');
    if (restore) {
      event.preventDefault();
      this.restore(restore.getAttribute('data-sfl-restore'), restore);
      return;
    }

    const drop = target.closest('[data-sfl-drop]');
    if (drop) {
      event.preventDefault();
      const key = drop.getAttribute('data-sfl-drop');
      const entry = store.list().find((item) => item.k === key);
      store.drop(key);
      if (entry) store.track('Saved Item Removed', { title: entry.t, variant_id: entry.vid });
      return;
    }

    const saveControl = target.closest('[data-sfl-save]');
    if (saveControl) {
      event.preventDefault();
      this.saveLine(saveControl.closest('[data-sfl-line]'));
      return;
    }
  }

  dispatchCartUpdate(payload) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: payload && typeof payload.total_price === 'number' ? payload : null,
          sourceId: 'saved-for-later',
          data: { source: 'saved-for-later', sections: payload?.sections, itemCount: payload?.item_count },
        },
      })
    );
  }

  showError(message) {
    const banner = this.root.querySelector?.('[ref="cartError"], .cart-drawer__error');
    const text = banner?.querySelector('.cart-drawer__error-text');
    if (banner && text) {
      banner.hidden = false;
      text.textContent = message;
      this.errorBanner = banner;
      return true;
    }
    const local = this.querySelector('[data-sfl-panel-error]');
    if (!local) return false;
    local.hidden = false;
    local.textContent = message;
    this.errorBanner = local;
    return true;
  }

  clearError() {
    if (!this.errorBanner) return;
    this.errorBanner.hidden = true;
    this.errorBanner = null;
    if (this.mode === 'inline') this.render();
  }

  /* ------------------------------------------------------------ save a line */

  entryFromRow(row) {
    let properties = null;
    try {
      properties = normalizeProperties(JSON.parse(row.getAttribute('data-sfl-properties') || 'null'));
    } catch {
      properties = null;
    }
    const rawPlan = row.getAttribute('data-sfl-selling-plan');
    const sellingPlan = rawPlan ? Number(rawPlan) : null;
    const variantId = row.getAttribute('data-sfl-variant');

    return {
      k: store.itemKey(variantId, sellingPlan, properties),
      vid: Number(variantId),
      pid: Number(row.getAttribute('data-sfl-product')) || null,
      q: Number(row.getAttribute('data-sfl-quantity')) || 1,
      sp: sellingPlan,
      pr: properties,
      t: row.getAttribute('data-sfl-title') || '',
      vt: row.getAttribute('data-sfl-variant-title') || '',
      vendor: row.getAttribute('data-sfl-vendor') || '',
      p: Number(row.getAttribute('data-sfl-price')) || 0,
      img: row.getAttribute('data-sfl-image') || '',
      u: row.getAttribute('data-sfl-url') || '',
      src: this.mode === 'swap' ? 'drawer' : 'cart',
    };
  }

  async saveLine(row) {
    if (!row) return;
    const lineKey = row.getAttribute('data-key');
    const entry = this.entryFromRow(row);

    let result;
    try {
      result = store.save(entry);
    } catch {
      this.showError(this.strings.save_failed);
      return;
    }

    const previous = row.previousElementSibling?.getAttribute('data-key') || null;
    this.clearError();
    const focused = document.activeElement;
    const claimed = this.contains(focused) || row.contains(focused);
    row.hidden = true;

    const removed = await this.removeLine(lineKey);
    if (!removed) {
      store.take(entry.k);
      if (result.displaced) store.save(result.displaced, result.displaced.saved_at);
      if (result.evicted) store.save(result.evicted, result.evicted.saved_at);
      row.hidden = false;
      this.showError(this.strings.save_failed);
      if (claimed) row.querySelector('[data-sfl-save]')?.focus();
      return;
    }

    this.announceSaved(Boolean(result.displaced));
    // The drawer has its own recovery net for a tagged cart:update; the cart page has none.
    if (claimed && this.mode === 'inline') this.recoverFocus();
  }

  async removeLine(lineKey) {
    try {
      const response = await fetch(themeRoutes().cart_change_url || '/cart/change', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: cartSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) return false;
      this.dispatchCartUpdate(await response.json().catch(() => null));
      return true;
    } catch {
      return false;
    }
  }

  /**
   * @param {boolean} already
   */
  announceSaved(already) {
    const message = already
      ? this.strings.saved_already || this.strings.saved_confirmation
      : this.strings.saved_confirmation;
    const live = this.querySelector('[data-sfl-announce]');
    if (!live) return;

    // Blank first so repeat text still reads as a change; setTimeout, since rAF is dead in a background tab.
    const token = (this.announceToken = (this.announceToken || 0) + 1);
    live.textContent = '';
    setTimeout(() => {
      if (this.announceToken !== token) return;
      const node = this.querySelector('[data-sfl-announce]');
      if (node) node.textContent = message;
    }, 0);
  }

  /** Cart-page counterpart of the drawer's #restoreFocusIfLost: one shot, no state carried. */
  recoverFocus() {
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active && active !== document.body) return;
      const heading = this.panel?.querySelector('[data-sfl-heading]');
      if (!(heading instanceof HTMLElement) || heading.closest('[hidden]')) return;
      if (typeof heading.checkVisibility === 'function' && !heading.checkVisibility()) return;
      heading.focus();
    });
  }

  /**
   * JSON body, never FormData: a fetch wrapper has dropped selling_plan from multipart here.
   * @param {object} entry
   * @returns {Promise<{ ok: boolean, message?: string }>}
   */
  async addToCart(entry, pending) {
    try {
      const { response, data: payload } = await postCartAdd({
        id: entry.vid,
        quantity: Number(entry.q) || 1,
        sellingPlan: entry.sp,
        properties: normalizeProperties(entry.pr),
        url: (themeRoutes().cart_add_url || '/cart/add.js').replace(/\.js$/, ''),
      });
      // Out before the morph: the skeleton row has no id, and the cart morph keys by id.
      pending?.remove();
      if (!response.ok) {
        const reported =
          payload?.description ||
          (typeof payload?.errors === 'string' ? payload.errors : null) ||
          payload?.message;
        return { ok: false, message: reported || this.strings.restore_failed };
      }

      const line = Array.isArray(payload?.items) ? payload.items[0] : payload;
      const returnedPlan = line?.selling_plan_allocation?.selling_plan?.id ?? null;
      if (Number(entry.sp ?? 0) !== Number(returnedPlan ?? 0)) {
        return { ok: false, message: this.strings.plan_changed };
      }

      store.track('Saved Item Restored', { title: entry.t, variant_id: entry.vid });
      this.dispatchCartUpdate(payload);
      return { ok: true };
    } catch {
      return { ok: false, message: this.strings.restore_failed };
    }
  }

  async restore(key, button) {
    const entry = store.list().find((item) => item.k === key);
    if (!entry) return;

    button.disabled = true;
    const errorNode = button.closest('.sfl-item, .sfl-shelf')?.querySelector('[data-sfl-error]');
    if (errorNode) errorNode.hidden = true;

    // Cart skeleton when the cart list is on screen; otherwise (saved panel open, or the cart-page
    // panel sitting below the fold) mark the clicked row busy so feedback is where the shopper is.
    const pending = insertPendingRow(button, entry.img);
    const row = button.closest('.sfl-item, .sfl-shelf__slide');
    if (!pending && row) row.classList.add('is-restoring');
    button.setAttribute('aria-busy', 'true');

    const result = await this.addToCart(entry, pending);
    pending?.remove();
    row?.classList.remove('is-restoring');
    button.removeAttribute('aria-busy');
    if (result.ok) {
      store.take(key);
      this.clearError();
    } else if (errorNode) {
      errorNode.textContent = result.message || this.strings.restore_failed;
      errorNode.hidden = false;
    }
    button.disabled = false;
    if (!result.ok && button.isConnected) button.focus();
  }

  async onSubmit(event) {
    const form = event.target.closest('[data-sfl-reminder-form]');
    if (!form) return;
    event.preventDefault();

    const input = form.querySelector('input[type="email"]');
    const error = form.querySelector('[data-sfl-reminder-error]');
    const button = form.querySelector('button');
    const email = (input?.value || '').trim();
    if (!email) return;

    if (error) error.hidden = true;
    if (button) button.disabled = true;

    try {
      const done = await store.subscribeReminder(email);
      if (!done) throw new Error(this.strings.reminder_failed);
      this.render();
    } catch (failure) {
      if (error) {
        error.textContent =
          failure instanceof Error && failure.message === 'consent_required'
            ? this.strings.reminder_consent
            : this.strings.reminder_failed;
        error.hidden = false;
      }
    } finally {
      if (button) button.disabled = false;
    }
  }
}

if (!customElements.get('saved-for-later')) customElements.define('saved-for-later', SavedForLater);
